/**
 * Email Foundation v1 — Slice 3 — Public email preferences page.
 *
 * The page authenticates the visitor purely via a preference-center
 * token delivered in the URL fragment (`#token=...`).  Mirrors the
 * PasswordReset security envelope:
 *
 *   - Read token from `window.location.hash`
 *   - Copy into component memory
 *   - Immediately scrub the visible URL via `history.replaceState`
 *   - Never persist to localStorage / sessionStorage
 *   - Never include in analytics events / query strings / logs
 *   - Send in request body only
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useTranslation } from "@/lib/i18n";

const COPY = {
  en: {
    title: "Email preferences",
    lead: "Choose which Stockaily emails you'd like to receive.",
    invalid_token:
      "This preference link is invalid or has expired. Please request a new one from your Stockaily account.",
    no_token:
      "This preference link is missing its token. Please open the link from your email.",
    state_subscribed: "You are currently subscribed to Stockaily marketing emails.",
    state_unsubscribed: "You are currently unsubscribed from Stockaily marketing emails.",
    state_unknown: "Marketing preferences have not been set for this address.",
    topic_newsletter: "Stockaily newsletter",
    topic_newsletter_desc: "Occasional research summaries and market thoughts.",
    topic_product_updates: "Product updates",
    topic_product_updates_desc: "New features, changelog and reliability updates.",
    unsubscribe_all: "Unsubscribe from all marketing",
    resubscribe_all: "Re-subscribe to marketing",
    resubscribe_note:
      "By re-subscribing you agree to receive Stockaily marketing emails. Stockaily provides model-based research, not personal investment advice.",
    saved: "Preferences updated.",
    error: "We couldn't update your preferences. Please try again.",
    suppressed_note:
      "This address is currently on our delivery-suppression list (bounce or complaint on record). Marketing emails will not be sent even if you subscribe.",
    back: "Back to Stockaily",
  },
  de: {
    title: "E-Mail-Einstellungen",
    lead: "Wählen Sie, welche Stockaily-E-Mails Sie erhalten möchten.",
    invalid_token:
      "Dieser Einstellungs-Link ist ungültig oder abgelaufen. Bitte fordern Sie einen neuen aus Ihrem Stockaily-Konto an.",
    no_token:
      "Diesem Link fehlt der Token. Bitte öffnen Sie den Link aus Ihrer E-Mail.",
    state_subscribed:
      "Sie erhalten derzeit Stockaily-Marketing-E-Mails.",
    state_unsubscribed:
      "Sie erhalten derzeit keine Stockaily-Marketing-E-Mails.",
    state_unknown:
      "Für diese Adresse sind keine Marketing-Einstellungen hinterlegt.",
    topic_newsletter: "Stockaily-Newsletter",
    topic_newsletter_desc:
      "Gelegentliche Research-Zusammenfassungen und Markt-Notizen.",
    topic_product_updates: "Produkt-Updates",
    topic_product_updates_desc:
      "Neue Funktionen, Changelog und Zuverlässigkeit.",
    unsubscribe_all: "Von allen Marketing-E-Mails abmelden",
    resubscribe_all: "Marketing-E-Mails wieder abonnieren",
    resubscribe_note:
      "Mit dem erneuten Abonnement erklären Sie sich einverstanden, Stockaily-Marketing-E-Mails zu erhalten. Stockaily bietet modellbasierte Recherche, keine persönliche Anlageberatung.",
    saved: "Einstellungen aktualisiert.",
    error: "Einstellungen konnten nicht aktualisiert werden. Bitte erneut versuchen.",
    suppressed_note:
      "Diese Adresse ist derzeit auf unserer Zustell-Sperrliste (Bounce oder Beschwerde vorhanden). Marketing-E-Mails werden nicht versendet, auch wenn Sie sich anmelden.",
    back: "Zurück zu Stockaily",
  },
};

function useCopy() {
  const { lang } = useTranslation();
  return COPY[lang === "de" ? "de" : "en"];
}

const CARD =
  "w-full max-w-[520px] bg-[#161B22] border border-white/10 rounded-2xl p-7 shadow-2xl";

export default function EmailPreferences() {
  const copy = useCopy();
  const tokenRef = useRef("");
  const [tokenRead, setTokenRead] = useState(false);
  const [hasToken, setHasToken] = useState(false);
  const [state, setState] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  useEffect(() => {
    let extracted = "";
    try {
      const raw = window.location.hash || "";
      const match = raw.match(/(?:^#|&)token=([^&]+)/);
      if (match && match[1]) {
        extracted = decodeURIComponent(match[1]);
      }
    } catch (_e) {
      extracted = "";
    }
    tokenRef.current = extracted;
    setHasToken(Boolean(extracted));
    // Scrub the URL immediately — this is critical for privacy.
    try {
      const cleanUrl = window.location.pathname + window.location.search;
      window.history.replaceState({}, "", cleanUrl);
    } catch (_e) {
      /* noop */
    }
    setTokenRead(true);
  }, []);

  useEffect(() => {
    if (!tokenRead || !tokenRef.current) return;
    (async () => {
      try {
        const resp = await api.post("/email/preferences/state", {
          token: tokenRef.current,
        });
        setState(resp?.data || null);
      } catch (_e) {
        setError("invalid_token");
      }
    })();
  }, [tokenRead]);

  async function callAndRefresh(path, extraBody = {}) {
    if (!tokenRef.current || busy) return;
    setBusy(true);
    setError("");
    try {
      const resp = await api.post(path, {
        token: tokenRef.current,
        ...extraBody,
      });
      setState(resp?.data || null);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2500);
    } catch (_e) {
      setError("generic");
    } finally {
      setBusy(false);
    }
  }

  if (!tokenRead) {
    return (
      <Shell data-testid="email-preferences-loading">
        <div className={CARD} />
      </Shell>
    );
  }

  if (!hasToken) {
    return (
      <Shell data-testid="email-preferences-no-token">
        <div className={CARD}>
          <h1 className="text-white text-xl font-semibold">{copy.title}</h1>
          <p className="mt-3 text-white/70 text-sm">{copy.no_token}</p>
          <BackLink label={copy.back} />
        </div>
      </Shell>
    );
  }

  if (error === "invalid_token") {
    return (
      <Shell data-testid="email-preferences-invalid">
        <div className={CARD}>
          <h1 className="text-white text-xl font-semibold">{copy.title}</h1>
          <p className="mt-3 text-white/70 text-sm">{copy.invalid_token}</p>
          <BackLink label={copy.back} />
        </div>
      </Shell>
    );
  }

  const stateLabel = (() => {
    if (!state) return "";
    if (state.state === "subscribed") return copy.state_subscribed;
    if (state.state === "unsubscribed") return copy.state_unsubscribed;
    return copy.state_unknown;
  })();

  const isSubscribed = state?.state === "subscribed";

  return (
    <Shell data-testid="email-preferences-page">
      <div className={CARD}>
        <h1 className="text-white text-xl font-semibold">{copy.title}</h1>
        <p className="mt-1.5 text-white/60 text-sm">{copy.lead}</p>
        {stateLabel ? (
          <div
            className="mt-4 rounded-lg bg-white/5 border border-white/10 text-white/80 text-sm p-3"
            data-testid="email-preferences-state-label"
          >
            {stateLabel}
          </div>
        ) : null}
        {state?.deliverability_suppressed ? (
          <div
            className="mt-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30 text-yellow-200 text-sm p-3"
            data-testid="email-preferences-suppressed-note"
          >
            {copy.suppressed_note}
          </div>
        ) : null}
        {savedFlash ? (
          <div
            className="mt-3 rounded-lg bg-[#3FAE89]/10 border border-[#3FAE89]/30 text-[#4bc39a] text-sm p-3"
            data-testid="email-preferences-saved"
          >
            {copy.saved}
          </div>
        ) : null}
        {error === "generic" ? (
          <div
            className="mt-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm p-3"
            data-testid="email-preferences-error"
          >
            {copy.error}
          </div>
        ) : null}
        <div className="mt-5 space-y-3">
          <TopicRow
            testid="topic-newsletter"
            title={copy.topic_newsletter}
            desc={copy.topic_newsletter_desc}
            checked={Boolean(state?.topics?.newsletter)}
            disabled={busy || !isSubscribed}
            onToggle={(v) =>
              callAndRefresh("/email/preferences/topic", {
                topic: "newsletter",
                enabled: v,
              })
            }
          />
          <TopicRow
            testid="topic-product-updates"
            title={copy.topic_product_updates}
            desc={copy.topic_product_updates_desc}
            checked={Boolean(state?.topics?.product_updates)}
            disabled={busy || !isSubscribed}
            onToggle={(v) =>
              callAndRefresh("/email/preferences/topic", {
                topic: "product_updates",
                enabled: v,
              })
            }
          />
        </div>
        <div className="mt-6 border-t border-white/10 pt-5">
          {isSubscribed ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => callAndRefresh("/email/preferences/unsubscribe")}
              className="w-full rounded-lg border border-white/20 text-white/80 hover:text-white hover:border-white/40 px-4 py-2.5 text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="email-preferences-unsubscribe"
            >
              {copy.unsubscribe_all}
            </button>
          ) : (
            <div>
              <p className="text-white/50 text-xs mb-2">
                {copy.resubscribe_note}
              </p>
              <button
                type="button"
                disabled={busy}
                onClick={() =>
                  callAndRefresh("/email/preferences/subscribe", {
                    topics: ["newsletter", "product_updates"],
                  })
                }
                className="w-full rounded-lg bg-[#3FAE89] hover:bg-[#4bc39a] text-[#0F1216] font-semibold px-4 py-2.5 text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="email-preferences-resubscribe"
              >
                {copy.resubscribe_all}
              </button>
            </div>
          )}
        </div>
        <BackLink label={copy.back} />
      </div>
    </Shell>
  );
}

function Shell({ children, "data-testid": testId }) {
  return (
    <div
      className="min-h-screen bg-[#0F1216] flex items-center justify-center p-4"
      data-testid={testId}
    >
      {children}
    </div>
  );
}

function TopicRow({ testid, title, desc, checked, disabled, onToggle }) {
  return (
    <label
      className="flex items-start gap-3 rounded-lg border border-white/10 bg-white/[0.03] p-3 cursor-pointer"
      data-testid={testid}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onToggle(e.target.checked)}
        className="mt-1 h-4 w-4 accent-[#3FAE89]"
        data-testid={`${testid}-input`}
      />
      <span className="flex-1">
        <span className="block text-white text-sm font-medium">{title}</span>
        <span className="block text-white/50 text-xs mt-0.5">{desc}</span>
      </span>
    </label>
  );
}

function BackLink({ label }) {
  return (
    <Link
      to="/"
      className="block text-center mt-5 text-sm text-white/60 hover:text-white transition-colors"
      data-testid="email-preferences-back"
    >
      {label}
    </Link>
  );
}
